'use client';

import { FileText, Star, MessageCircle, History, Bell, MessageCircleQuestion } from 'lucide-react';
import { Work } from '@/types/work';
import { WorkMetadata } from '@/services/metadata.service';
import { useState, useEffect, useMemo } from 'react';
import { Tabs } from '@/components/ui/Tabs';
import { usePathname } from 'next/navigation';
import AnalyticsService, { LogEvent } from '@/services/analytics.service';
import { buildPayloadForDocumentTabClick } from '@/types/analytics';
import { useUser } from '@/contexts/UserContext';
import { useDeviceType } from '@/hooks/useDeviceType';
import { useCurrencyPreference } from '@/contexts/CurrencyPreferenceContext';
import { useExchangeRate } from '@/contexts/ExchangeRateContext';
import Icon from '@/components/ui/icons/Icon';
import { ResearchCoinIcon } from '@/components/ui/icons/ResearchCoinIcon';
import { getActiveBounties, getTotalBountyDisplayAmount } from '@/components/Bounty/lib/bountyUtil';
import { formatCurrency } from '@/utils/currency';
import { colors } from '@/app/styles/colors';

export type TabType =
  | 'paper'
  | 'updates'
  | 'reviews'
  | 'bounties'
  | 'conversation'
  | 'history'
  | 'applications';

interface WorkTabsProps {
  work: Work;
  metadata: WorkMetadata;
  defaultTab?: TabType;
  contentType?: 'paper' | 'post' | 'fund';
  onTabChange: (tab: TabType) => void;
  updatesCount?: number;
}

export const WorkTabs = ({
  work,
  metadata,
  contentType = 'paper',
  onTabChange,
  updatesCount = 0,
}: WorkTabsProps) => {
  const pathname = usePathname();
  const { user } = useUser();
  const deviceType = useDeviceType();
  const { showUSD } = useCurrencyPreference();
  const { exchangeRate, isLoading: isExchangeRateLoading } = useExchangeRate();

  const reviewCount = work.peerReviews?.length ?? 0;

  const activeBounties = useMemo(
    () => getActiveBounties(metadata.bounties || []),
    [metadata.bounties]
  );
  const { amount: totalBountyAmount } = useMemo(
    () => getTotalBountyDisplayAmount(activeBounties, exchangeRate, showUSD),
    [activeBounties, exchangeRate, showUSD]
  );

  const canDisplayBountyAmount =
    !showUSD || (showUSD && !isExchangeRateLoading && exchangeRate > 0);

  const hasActiveBounties = activeBounties.length > 0;

  const hasResearchHubJournalVersions = useMemo(() => {
    return (work.versions || []).some((version) => version.isResearchHubJournal);
  }, [work.versions]);

  const getActiveTabFromPath = (path: string): TabType => {
    if (path.includes('/updates')) return 'updates';
    if (path.includes('/conversation')) return 'conversation';
    if (path.includes('/applications')) return 'applications';
    if (path.includes('/reviews')) return 'reviews';
    if (path.includes('/bounties')) return 'bounties';
    if (path.includes('/history') && hasResearchHubJournalVersions) return 'history';
    return 'paper';
  };

  const [activeTab, setActiveTab] = useState<TabType>(() => getActiveTabFromPath(pathname));

  useEffect(() => {
    const tabFromPath = getActiveTabFromPath(pathname);
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
      onTabChange(tabFromPath);
    }
  }, [pathname, onTabChange, hasResearchHubJournalVersions]);

  const handleTabChange = (tab: TabType) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    onTabChange(tab);

    try {
      const payload = buildPayloadForDocumentTabClick(work, {
        clickedTab: tab,
        deviceType,
      });
      AnalyticsService.logEventWithUserProperties(LogEvent.DOCUMENT_TAB_CLICKED, payload, user);
    } catch (error) {
      console.warn('Failed to track document tab click analytics:', error);
    }

    if (typeof window !== 'undefined') {
      const baseUrl =
        contentType === 'paper'
          ? `/paper/${work.id}/${work.slug}`
          : contentType === 'fund'
            ? `/proposal/${work.id}/${work.slug}`
            : work.postType === 'QUESTION'
              ? `/question/${work.id}/${work.slug}`
              : `/post/${work.id}/${work.slug}`;

      const tabUrlMap: Partial<Record<TabType, string>> = {
        updates: `${baseUrl}/updates`,
        conversation: `${baseUrl}/conversation`,
        reviews: `${baseUrl}/reviews`,
        applications: `${baseUrl}/applications`,
        bounties: `${baseUrl}/bounties`,
        history: `${baseUrl}/history`,
      };
      const newUrl = tabUrlMap[tab] || baseUrl;

      window.history.replaceState(null, '', newUrl);
    }
  };

  // Get the appropriate label for the main content tab
  const getMainTabLabel = () => {
    if (contentType === 'paper') return 'Paper';
    if (contentType === 'fund') return 'Project';
    if (work.postType === 'QUESTION') return 'Question';
    return 'Post';
  };

  // Define base tabs
  const baseTabs = [
    {
      id: 'paper',
      label: (
        <div className="flex items-center">
          {work.postType === 'QUESTION' ? (
            <MessageCircleQuestion className="h-4 w-4 mr-2" />
          ) : (
            <FileText className="h-4 w-4 mr-2" />
          )}
          {getMainTabLabel()}
        </div>
      ),
    },
    // Show Updates tab only for fund content type
    ...(contentType === 'fund'
      ? [
          {
            id: 'updates',
            label: (
              <div className="flex items-center">
                <Bell className="h-4 w-4 mr-2" />
                <span>Updates</span>
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === 'updates'
                      ? 'bg-indigo-100 text-indigo-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {updatesCount}
                </span>
              </div>
            ),
          },
        ]
      : []),
    // Show Reviews tab only if not a question
    ...(work.postType === 'QUESTION'
      ? []
      : [
          {
            id: 'reviews',
            label: (
              <div className="flex items-center">
                <Star
                  className={`h-4 w-4 mr-2 ${
                    activeTab === 'reviews' ? 'text-primary-500' : 'text-gray-500'
                  }`}
                />
                <span>Reviews</span>
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === 'reviews'
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {reviewCount}
                </span>
              </div>
            ),
          },
        ]),
    {
      id: 'conversation',
      label: (
        <div className="flex items-center">
          <MessageCircle className="h-4 w-4 mr-2" />
          <span>{work.postType === 'QUESTION' ? 'Answers' : 'Conversation'}</span>
          <span
            className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
              activeTab === 'conversation'
                ? 'bg-primary-100 text-primary-600'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {metadata.metrics.conversationComments || 0}
          </span>
        </div>
      ),
    },
    {
      id: 'bounties',
      label: (
        <div className="flex items-center">
          <Icon
            name={activeTab === 'bounties' ? 'solidEarn' : 'earn1'}
            size={16}
            color={activeTab === 'bounties' ? colors.primary[500] : colors.gray[500]}
          />
          <span className="ml-2">Bounties</span>
          <span
            className={`ml-2 py-0.5 px-2 rounded-full text-xs flex items-center gap-0.5 ${
              activeTab === 'bounties'
                ? 'bg-primary-100 text-primary-600'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {hasActiveBounties && canDisplayBountyAmount ? (
              <>
                {!showUSD && (
                  <ResearchCoinIcon
                    size={12}
                    color={activeTab === 'bounties' ? colors.primary[500] : colors.gray[500]}
                    outlined
                  />
                )}
                {formatCurrency({
                  amount: totalBountyAmount,
                  showUSD,
                  exchangeRate,
                  shorten: true,
                  skipConversion: showUSD,
                })}
              </>
            ) : (
              0
            )}
          </span>
        </div>
      ),
    },
  ];

  const tabs = useMemo(() => {
    if (hasResearchHubJournalVersions) {
      return [
        ...baseTabs,
        {
          id: 'history',
          label: (
            <div className="flex items-center">
              <History className="h-4 w-4 mr-2" />
              <span>History</span>
              <span
                className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'history'
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {work.versions?.length || 0}
              </span>
            </div>
          ),
        },
      ];
    }
    return baseTabs;
  }, [baseTabs, hasResearchHubJournalVersions, activeTab, work.versions?.length]);

  return (
    <Tabs
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(id) => handleTabChange(id as TabType)}
      className="border-b-0"
    />
  );
};
